/**
 * Assignment engine — canAssign / score / persist.
 *
 * Hard rejects block assign. Soft score ranks legal tails. Automatic mode
 * picks the best legal assignment or leaves the request in Inbox.
 * Dispatcher / voice (Megan, later) call the same functions via assignmentOps.
 *
 * Multi-pilot company: different trips / routes to different pilots (typically
 * different tails). One open assignment per request. A second PIC is still
 * blocked. Dual-pilot complement stores an SIC on the same assignment when
 * company ops settings require two crew.
 */
import { hydrateSceneryAirport, resolveCoords } from "./airports";
import { isNarniaGroundSpawn } from "./flightBinding";
import {
  type AircraftState,
  deriveAircraftState,
  emptyAircraftState,
  isAirbornePhase,
} from "./aircraftState";
import { crewIdFromName, dutyViewFor, type DutyRoster, type DutyView } from "./duty";
import { computeLeg, eteMinutes } from "./generator";
import { planningCruiseKt, runwayPicPairWarning } from "./hawker800xp";
import { haversineNm } from "./geo";
import { canCarryMxPayload, destIsMroForType } from "./charterRealism";
import { isCharterAssignable } from "./maintenance";
import { aircraftMatchesRatings } from "./profile";
import { withLegs } from "./schedule";
import type {
  AircraftSpec,
  CancelReasonCode,
  CharterRequest,
  CompanyMxBase,
  DraftMission,
  InboxRequestStatus,
  Mission,
  OpsDeskSettings,
  PilotProfile,
  SceneryAirport,
} from "./types";
import { stampCancel } from "./cancelReason";
import { hydrateCrewRoutePrefs, requestPrefContext, scoreCrewPrefMatch } from "./crewPrefs";
import { evaluateTripConstraints } from "./opsConstraints";
import { hourlyCostTieBreak, scoreAircraftOperatingFit } from "./aircraftOperatingCost";

export type AssignmentStatus = "pending" | "accepted" | "declined" | "held";

export type AssignmentSource = "auto-agent" | "dispatcher" | "voice" | "pilot";

export type AssignmentReasonCode =
  | "cannot_fly_time"
  | "weather"
  | "aircraft"
  | "personal"
  | "defer"
  | "other";

export const ASSIGNMENT_REASON_LABEL: Record<AssignmentReasonCode, string> = {
  cannot_fly_time: "Cannot fly due to time / duty",
  weather: "Weather",
  aircraft: "Aircraft / equipment",
  personal: "Personal",
  defer: "Defer / hold",
  other: "Other",
};

export type HardRejectCode =
  | "wrong_phase"
  | "insufficient_duty"
  | "insufficient_flight_time"
  | "duty_rest"
  | "double_booked"
  | "impossible_reposition"
  | "grounded"
  | "not_rated"
  | "cooldown"
  | "already_assigned"
  | "already_crewed"
  | "crew_double_booked"
  | "no_request"
  | "no_tail"
  | "under_crewed"
  | "curfew"
  | "customs"
  | "overweight"
  | "deice"
  | "convective_hold";

export type SoftFactorId =
  | "deadhead"
  | "reposition_time"
  | "turn"
  | "revenue"
  | "fatigue"
  | "same_field"
  | "on_duty"
  | "near_conflict"
  | "crew_load"
  | "crew_pref"
  | "runway_pic"
  | "duty_advisory"
  | "crew_minimum"
  | "curfew"
  | "customs"
  | "weight"
  | "request_fit"
  | "range_fit"
  | "time_fit"
  | "operating_cost";

export type HardReject = {
  code: HardRejectCode;
  message: string;
};

export type SoftFactor = {
  id: SoftFactorId;
  delta: number;
  note: string;
};

export type TripAssignment = {
  id: string;
  requestId: string;
  missionId?: string;
  ferryMissionId?: string;
  /** Empty return after an outbound charter (not revenue). */
  returnFerryMissionId?: string;
  aircraftId: string;
  registration: string;
  crewId: string;
  crewName: string;
  /** Second pilot when the company complement is dual. Empty until assigned. */
  sicCrewId?: string;
  sicCrewName?: string;
  status: AssignmentStatus;
  source: AssignmentSource;
  assignedAtUtc: string;
  decidedAtUtc?: string;
  reasonCode?: AssignmentReasonCode;
  /** Company settings decline code. The assignment enum stays for the handoff machine. */
  declineReasonId?: string;
  comment?: string;
  repositionRequired: boolean;
  repositionNm?: number;
  repositionEteMin?: number;
  superseded?: boolean;
};

export type AssignmentAuditEvent = {
  id: string;
  atUtc: string;
  actor: AssignmentSource | "pilot";
  action: "assign" | "reassign" | "accept" | "decline" | "hold" | "release" | "supersede";
  requestId: string;
  assignmentId?: string;
  aircraftId?: string;
  note?: string;
};

export type DeclineCooldown = {
  requestId: string;
  crewId: string;
  untilUtc: string;
};

export const DECLINE_COOLDOWN_MS = 2 * 60 * 60 * 1000;
export const MIN_TURN_MIN = 20;
export const PHASE_WINDOW_MIN = 45;

export type AssignTripInput = {
  request: CharterRequest;
  aircraft: AircraftSpec;
  aircraftState: AircraftState;
  missions: Mission[];
  assignments: TripAssignment[];
  duty: DutyView;
  scenery: SceneryAirport[];
  crewId: string;
  crewName: string;
  ratedIcaoTypes?: string[];
  cooldowns?: DeclineCooldown[];
  now?: Date;
  /** Trip ETE when the request has not been drafted yet. */
  eteMin?: number;
  /**
   * Default on. When false, rest / FDP / flight-time duty hits are advisory
   * (soft factors) instead of hard blocks.
   */
  enforceDutyClock?: boolean;
  /** Company MX bases. A dest counts as a shop only when it is on this list. */
  mxBases?: readonly CompanyMxBase[];
  /** When set, crew / curfew / customs / weight use company ops settings. */
  opsDesk?: OpsDeskSettings | null;
  /** SIC already named on this trip. Counts toward the crew minimum. */
  sicCrewId?: string | null;
  /** Planned fuel (lb) for the weight ballpark. Zero skips the fuel limit. */
  fuelLb?: number;
};

export type CanAssignResult = {
  ok: boolean;
  hard: HardReject[];
  score: number;
  factors: SoftFactor[];
  repositionRequired: boolean;
  repositionNm: number;
  repositionEteMin: number;
  currentIcao: string | null;
  originIcao: string;
};

export type RankedCandidate = CanAssignResult & {
  aircraft: AircraftSpec;
};

/** Company pilot eligible for a trip. Different trips → different pilots; never two pilots on one trip. */
export type CrewCandidate = {
  crewId: string;
  crewName: string;
  ratedIcaoTypes?: string[];
  duty: DutyView;
  /** Soft route / distance prefs. Empty = no assign bias. */
  routeAreaPrefs?: string;
  preferredTripNm?: number | null;
};

export type RankedPair = RankedCandidate & {
  crewId: string;
  crewName: string;
};

function newId(prefix: string, now: Date): string {
  return `${prefix}-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

export function isAssignmentStatus(value: unknown): value is AssignmentStatus {
  return value === "pending" || value === "accepted" || value === "declined" || value === "held";
}

export function isAssignmentSource(value: unknown): value is AssignmentSource {
  return value === "auto-agent" || value === "dispatcher" || value === "voice" || value === "pilot";
}

function cancelReasonFromAssignment(code: AssignmentReasonCode): CancelReasonCode {
  if (code === "cannot_fly_time" || code === "defer") return "scheduling";
  if (code === "weather" || code === "aircraft") return "operational";
  return "other";
}

export function isAssignmentReason(value: unknown): value is AssignmentReasonCode {
  return (
    value === "cannot_fly_time" ||
    value === "weather" ||
    value === "aircraft" ||
    value === "personal" ||
    value === "defer" ||
    value === "other"
  );
}

export function hydrateTripAssignment(raw: Partial<TripAssignment>): TripAssignment | null {
  if (!raw?.id || !raw.requestId || !raw.aircraftId) return null;
  const status = isAssignmentStatus(raw.status) ? raw.status : "pending";
  const source = isAssignmentSource(raw.source) ? raw.source : "dispatcher";
  const sicCrewId = raw.sicCrewId?.trim() && raw.sicCrewId.trim() !== (raw.crewId ?? "").trim() ? raw.sicCrewId.trim() : undefined;
  return {
    id: raw.id,
    requestId: raw.requestId,
    missionId: raw.missionId,
    ferryMissionId: raw.ferryMissionId,
    returnFerryMissionId: raw.returnFerryMissionId,
    aircraftId: raw.aircraftId,
    registration: raw.registration ?? "",
    crewId: raw.crewId ?? "",
    crewName: raw.crewName ?? "",
    sicCrewId,
    sicCrewName: sicCrewId ? raw.sicCrewName?.trim() || undefined : undefined,
    status,
    source,
    assignedAtUtc: raw.assignedAtUtc ?? new Date().toISOString(),
    decidedAtUtc: raw.decidedAtUtc,
    reasonCode: isAssignmentReason(raw.reasonCode) ? raw.reasonCode : undefined,
    declineReasonId: raw.declineReasonId?.trim().toLowerCase().slice(0, 32) || undefined,
    comment: raw.comment,
    repositionRequired: Boolean(raw.repositionRequired),
    repositionNm: raw.repositionNm,
    repositionEteMin: raw.repositionEteMin,
    superseded: Boolean(raw.superseded),
  };
}

export function hydrateTripAssignments(raw?: unknown): TripAssignment[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => hydrateTripAssignment(row as Partial<TripAssignment>)).filter(Boolean) as TripAssignment[];
}

export function hydrateAssignmentAudit(raw?: unknown): AssignmentAuditEvent[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row) => row && typeof row === "object" && typeof (row as AssignmentAuditEvent).id === "string")
    .map((row) => {
      const r = row as AssignmentAuditEvent;
      return {
        id: r.id,
        atUtc: r.atUtc ?? new Date().toISOString(),
        actor: r.actor === "auto-agent" || r.actor === "dispatcher" || r.actor === "voice" || r.actor === "pilot" ? r.actor : "dispatcher",
        action:
          r.action === "assign" ||
          r.action === "reassign" ||
          r.action === "accept" ||
          r.action === "decline" ||
          r.action === "hold" ||
          r.action === "release" ||
          r.action === "supersede"
            ? r.action
            : "assign",
        requestId: r.requestId ?? "",
        assignmentId: r.assignmentId,
        aircraftId: r.aircraftId,
        note: r.note,
      };
    });
}

export function hydrateDeclineCooldowns(raw?: unknown, now = new Date()): DeclineCooldown[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row) => row && typeof (row as DeclineCooldown).requestId === "string")
    .map((row) => ({
      requestId: (row as DeclineCooldown).requestId,
      crewId: (row as DeclineCooldown).crewId ?? "",
      untilUtc: (row as DeclineCooldown).untilUtc ?? now.toISOString(),
    }))
    .filter((row) => new Date(row.untilUtc).getTime() > now.getTime() - DECLINE_COOLDOWN_MS);
}

export function openAssignments(list: TripAssignment[]): TripAssignment[] {
  return list.filter((a) => !a.superseded && (a.status === "pending" || a.status === "held" || a.status === "accepted"));
}

export function pendingAssignmentsForCrew(list: TripAssignment[], crewId: string): TripAssignment[] {
  return list.filter(
    (a) => !a.superseded && (a.status === "pending" || a.status === "held") && (!crewId || a.crewId === crewId),
  );
}

export function assignmentForRequest(list: TripAssignment[], requestId: string): TripAssignment | undefined {
  return openAssignments(list).find((a) => a.requestId === requestId);
}

export function openAssignmentsForCrew(list: TripAssignment[], crewId: string): TripAssignment[] {
  return openAssignments(list).filter((a) => a.crewId === crewId);
}

/** One open assignment per request. Shared-cockpit (two pilots, one trip) is not shipped. */
export function hasOpenAssignmentForOtherCrew(
  list: TripAssignment[],
  requestId: string,
  crewId: string,
): TripAssignment | undefined {
  const open = assignmentForRequest(list, requestId);
  if (open && open.crewId && crewId && open.crewId !== crewId) return open;
  return undefined;
}

/** Open (or recently assigned) missions that belong to this crew — pilot desk focus. */
/** Charter + linked inbound / return ferries on one assignment. */
export function assignmentLinkedMissionIds(a: TripAssignment): string[] {
  return [a.missionId, a.ferryMissionId, a.returnFerryMissionId].filter((id): id is string => Boolean(id));
}

export function missionsForCrew(
  missions: Mission[],
  assignments: TripAssignment[],
  crewId: string,
): Mission[] {
  const id = crewId.trim();
  if (!id) return [];
  const mine = assignments.filter((a) => !a.superseded && a.crewId === id && a.status !== "declined");
  const missionIds = new Set<string>();
  for (const row of mine) {
    for (const mid of assignmentLinkedMissionIds(row)) missionIds.add(mid);
  }
  return missions.filter((m) => missionIds.has(m.id));
}

export function pilotSeesCompanySchedule(input: {
  role: "dispatcher" | "pilot";
  allowPilotsCompanySchedule?: boolean;
  pilotScheduleView?: string | null;
}): boolean {
  if (input.role === "dispatcher") return true;
  return input.allowPilotsCompanySchedule === true && input.pilotScheduleView === "company";
}

/** Dispatcher sees the full board. Pilot default is My trips only (assigned + linked ferries). */
export function deskMissionsForRole(
  missions: Mission[],
  assignments: TripAssignment[],
  input: {
    role: "dispatcher" | "pilot";
    crewId: string;
    allowCompanySchedule?: boolean;
    scheduleView?: string | null;
  },
): Mission[] {
  if (input.role === "dispatcher") return missions;
  if (
    pilotSeesCompanySchedule({
      role: "pilot",
      allowPilotsCompanySchedule: input.allowCompanySchedule,
      pilotScheduleView: input.scheduleView,
    })
  ) {
    return missions;
  }
  return missionsForCrew(missions, assignments, input.crewId);
}

export function assignmentForMission(
  assignments: TripAssignment[],
  missionId: string | null | undefined,
): TripAssignment | undefined {
  if (!missionId) return undefined;
  return openAssignments(assignments).find((a) => assignmentLinkedMissionIds(a).includes(missionId));
}

/** Accepted / pending trip that should feed the solo progress strip (route + ETA). */
export function activeAssignedTrip(
  assignments: TripAssignment[],
  missions: Mission[],
  opts?: { crewId?: string; registration?: string },
): { assignment: TripAssignment; mission: Mission } | null {
  const rank = (status: AssignmentStatus) => (status === "accepted" ? 0 : status === "pending" ? 1 : 2);
  const open = openAssignments(assignments)
    .filter((a) => {
      if (opts?.crewId && a.crewId && a.crewId !== opts.crewId) return false;
      if (opts?.registration && a.registration && a.registration.toUpperCase() !== opts.registration.toUpperCase()) {
        return false;
      }
      return Boolean(a.missionId);
    })
    .sort((a, b) => rank(a.status) - rank(b.status));
  for (const assignment of open) {
    const linked = assignmentLinkedMissionIds(assignment)
      .map((mid) => missions.find((m) => m.id === mid && m.status !== "cancelled" && m.status !== "completed"))
      .filter((m): m is Mission => Boolean(m))
      .sort((a, b) => {
        const liveA = a.status === "in-progress" || a.lifecycle === "airborne" || a.lifecycle === "taxi" ? 0 : 1;
        const liveB = b.status === "in-progress" || b.lifecycle === "airborne" || b.lifecycle === "taxi" ? 0 : 1;
        if (liveA !== liveB) return liveA - liveB;
        return new Date(a.departureIso).getTime() - new Date(b.departureIso).getTime();
      });
    const mission = linked[0];
    if (mission) return { assignment, mission };
  }
  return null;
}

export function isOnCooldown(
  cooldowns: DeclineCooldown[] | undefined,
  requestId: string,
  crewId: string,
  now: Date,
): boolean {
  return (cooldowns ?? []).some(
    (c) => c.requestId === requestId && c.crewId === crewId && new Date(c.untilUtc).getTime() > now.getTime(),
  );
}

export function addDeclineCooldown(
  cooldowns: DeclineCooldown[],
  requestId: string,
  crewId: string,
  now: Date,
  ms = DECLINE_COOLDOWN_MS,
): DeclineCooldown[] {
  const untilUtc = new Date(now.getTime() + ms).toISOString();
  const rest = cooldowns.filter((c) => !(c.requestId === requestId && c.crewId === crewId));
  return [...rest, { requestId, crewId, untilUtc }];
}

function windowsOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd;
}

function missionWindow(m: Pick<Mission, "departureIso" | "eteMin">, padMin = MIN_TURN_MIN): { start: number; end: number } {
  const start = new Date(m.departureIso).getTime() - padMin * 60_000;
  const end = new Date(m.departureIso).getTime() + Math.max(25, m.eteMin) * 60_000 + padMin * 60_000;
  return { start, end };
}

export function repositionBetween(
  fromIcao: string | null | undefined,
  toIcao: string,
  spec: AircraftSpec,
  scenery: SceneryAirport[],
): { nm: number; eteMin: number } {
  const from = fromIcao?.trim().toUpperCase();
  const to = toIcao.trim().toUpperCase();
  if (!from || from === to) return { nm: 0, eteMin: 0 };
  const oc = resolveCoords(from, scenery);
  const dc = resolveCoords(to, scenery);
  if (!oc || !dc) return { nm: 0, eteMin: 0 };
  const nm = haversineNm(oc.lat, oc.lon, dc.lat, dc.lon);
  return { nm, eteMin: eteMinutes(nm, planningCruiseKt(spec, nm)) };
}

export function estimateRequestEte(request: CharterRequest, spec: AircraftSpec, scenery: SceneryAirport[]): number {
  const hops = (request.proposedLegs ?? []).filter(
    (leg) => !leg.empty && leg.originIcao !== "TBD" && leg.destIcao !== "TBD",
  );
  if (hops.length > 1) {
    let total = 0;
    for (const hop of hops) {
      const oc = resolveCoords(hop.originIcao, scenery);
      const dc = resolveCoords(hop.destIcao, scenery);
      if (!oc || !dc) continue;
      const nm = haversineNm(oc.lat, oc.lon, dc.lat, dc.lon);
      total += eteMinutes(nm, planningCruiseKt(spec, nm));
    }
    if (total > 0) return total;
  }
  const oc = resolveCoords(request.originIcao, scenery);
  const dc = resolveCoords(request.destIcao, scenery);
  if (!oc || !dc) return 90;
  const nm = haversineNm(oc.lat, oc.lon, dc.lat, dc.lon);
  return eteMinutes(nm, planningCruiseKt(spec, nm));
}

function hopNm(fromIcao: string, toIcao: string, scenery: SceneryAirport[]): number {
  const from = fromIcao.trim().toUpperCase();
  const to = toIcao.trim().toUpperCase();
  if (!from || !to || from === "TBD" || to === "TBD" || from === to) return 0;
  const oc = resolveCoords(from, scenery);
  const dc = resolveCoords(to, scenery);
  if (!oc || !dc) return 0;
  return haversineNm(oc.lat, oc.lon, dc.lat, dc.lon);
}

function requestHopNm(request: CharterRequest, scenery: SceneryAirport[]): { maxHopNm: number; tripNm: number } {
  const legs = (request.proposedLegs ?? []).filter((leg) => !leg.empty && leg.originIcao && leg.destIcao);
  const pairs = legs.length
    ? legs.map((leg) => [leg.originIcao, leg.destIcao] as const)
    : [[request.originIcao, request.destIcao] as const];
  let maxHopNm = 0;
  let tripNm = 0;
  for (const [from, to] of pairs) {
    const nm = hopNm(from, to, scenery);
    if (nm <= 0) continue;
    tripNm += nm;
    if (nm > maxHopNm) maxHopNm = nm;
  }
  return { maxHopNm, tripNm };
}

function currentField(state: AircraftState, spec: AircraftSpec): string | null {
  return state.currentIcao ?? spec.lastAirportIcao?.toUpperCase() ?? state.parkedIcao ?? null;
}

/**
 * Where the tail will be when this trip's STD arrives: dest of the last
 * pending / in-progress leg that finishes before then. Parked / live field
 * is the fallback when the board is empty.
 */
export function projectedTailIcao(input: {
  aircraftId: string;
  missions: ReadonlyArray<
    Pick<Mission, "aircraftId" | "status" | "departureIso" | "eteMin" | "destIcao"> &
      Partial<Pick<Mission, "originIcao" | "flightType" | "isMaintenance">>
  >;
  beforeUtc: number;
  fallbackIcao: string | null;
  /**
   * Empty ferries leaving this field are ignored. Used when the next revenue
   * already originates here — do not project home just to ferry back.
   */
  ignoreEmptyFerriesFromIcao?: string | null;
}): string | null {
  const skipOrigin = (input.ignoreEmptyFerriesFromIcao ?? "").trim().toUpperCase();
  const prior = input.missions
    .filter((m) => m.aircraftId === input.aircraftId)
    .filter((m) => m.status === "pending" || m.status === "in-progress")
    .filter((m) => {
      if (!skipOrigin) return true;
      if (m.flightType !== "ferry" || m.isMaintenance) return true;
      return (m.originIcao ?? "").trim().toUpperCase() !== skipOrigin;
    })
    .map((m) => {
      const start = new Date(m.departureIso).getTime();
      const end = start + Math.max(25, m.eteMin) * 60_000;
      return { dest: m.destIcao?.toUpperCase() ?? "", end };
    })
    .filter((row) => Number.isFinite(row.end) && row.end <= input.beforeUtc && row.dest)
    .sort((a, b) => a.end - b.end);
  const last = prior[prior.length - 1];
  return last?.dest ?? input.fallbackIcao;
}

export function canAssign(input: AssignTripInput): CanAssignResult {
  const now = input.now ?? new Date();
  const originIcao = input.request.originIcao.toUpperCase();
  const destIcao = input.request.destIcao.toUpperCase();
  const parkedIcao = currentField(input.aircraftState, input.aircraft);
  const std = new Date(input.request.departureIso).getTime();
  const tripStart = Number.isFinite(std) ? std : now.getTime() + 90 * 60_000;
  const currentIcao = projectedTailIcao({
    aircraftId: input.aircraft.id,
    missions: input.missions,
    beforeUtc: tripStart,
    fallbackIcao: parkedIcao,
    ignoreEmptyFerriesFromIcao: originIcao,
  });
  const hard: HardReject[] = [];
  const factors: SoftFactor[] = [];
  const eteMin = input.eteMin ?? estimateRequestEte(input.request, input.aircraft, input.scenery);
  const repo = repositionBetween(currentIcao, originIcao, input.aircraft, input.scenery);
  const tripEnd = tripStart + eteMin * 60_000;

  if (!input.request.id) hard.push({ code: "no_request", message: "No inbox request." });
  if (!input.aircraft.id) hard.push({ code: "no_tail", message: "No tail." });

  const mxPayloadOk =
    Boolean(input.request.mxPositioning) &&
    canCarryMxPayload(input.aircraft) &&
    destIsMroForType(destIcao, input.aircraft.icaoType, input.mxBases);
  if (!isCharterAssignable(input.aircraft) && !mxPayloadOk) {
    hard.push({ code: "grounded", message: `${input.aircraft.registration} is not assignable (${input.aircraft.status}).` });
  }
  if (input.ratedIcaoTypes && !aircraftMatchesRatings(input.aircraft, input.ratedIcaoTypes)) {
    hard.push({ code: "not_rated", message: `${input.aircraft.registration} is not on the company pilot rating.` });
  }

  const openOnRequest = assignmentForRequest(input.assignments, input.request.id);
  if (openOnRequest && openOnRequest.aircraftId !== input.aircraft.id) {
    hard.push({
      code: "already_assigned",
      message: `Request already assigned to ${openOnRequest.registration} (${openOnRequest.status}). Reassign supersedes.`,
    });
  }
  if (openOnRequest && openOnRequest.crewId && input.crewId && openOnRequest.crewId !== input.crewId) {
    hard.push({
      code: "already_crewed",
      message: `Trip already assigned to ${openOnRequest.crewName || openOnRequest.crewId}. One pilot per trip — shared cockpit is not supported.`,
    });
  }

  if (isOnCooldown(input.cooldowns, input.request.id, input.crewId, now)) {
    hard.push({ code: "cooldown", message: "Decline cooldown — do not immediately re-auto-assign this trip to the same crew." });
  }

  const overlapping = input.missions.filter((m) => {
    if (m.aircraftId !== input.aircraft.id) return false;
    if (m.status === "completed" || m.status === "cancelled") return false;
    const w = missionWindow(m);
    return windowsOverlap(w.start, w.end, tripStart - MIN_TURN_MIN * 60_000, tripEnd + MIN_TURN_MIN * 60_000);
  });
  if (overlapping.length) {
    hard.push({
      code: "double_booked",
      message: `${input.aircraft.registration} is already on ${overlapping[0]!.callsign} ${overlapping[0]!.originIcao}–${overlapping[0]!.destIcao}.`,
    });
  }

  const crewBusy = openAssignmentsForCrew(input.assignments, input.crewId).filter((a) => a.requestId !== input.request.id);
  const crewOverlap = crewBusy.find((a) => {
    const m = a.missionId ? input.missions.find((row) => row.id === a.missionId) : undefined;
    if (!m || m.status === "completed" || m.status === "cancelled") return false;
    const w = missionWindow(m);
    return windowsOverlap(w.start, w.end, tripStart - MIN_TURN_MIN * 60_000, tripEnd + MIN_TURN_MIN * 60_000);
  });
  if (crewOverlap) {
    hard.push({
      code: "crew_double_booked",
      message: `${input.crewName || input.crewId} is already assigned ${crewOverlap.registration} on another trip in this window.`,
    });
  }

  const minutesToStd = (tripStart - now.getTime()) / 60_000;
  if (repo.eteMin > 0 && minutesToStd < repo.eteMin + MIN_TURN_MIN) {
    hard.push({
      code: "impossible_reposition",
      message: `Cannot reposition ${currentIcao ?? "current field"}→${originIcao} in ${Math.max(0, Math.round(minutesToStd))} min (needs ${repo.eteMin + MIN_TURN_MIN} incl. turn).`,
    });
  }

  const phase = input.aircraftState.phase;
  if (isAirbornePhase(phase) && minutesToStd < PHASE_WINDOW_MIN) {
    const headingHome = destIcao && currentIcao && originIcao === destIcao;
    const atOrigin = currentIcao === originIcao;
    if (!atOrigin && !headingHome) {
      hard.push({
        code: "wrong_phase",
        message: `Aircraft is ${input.aircraftState.label} — cannot make ${originIcao} in the trip window.`,
      });
    }
  }
  if ((phase === "taxi" || phase === "takeoff_climb") && minutesToStd < 20 && currentIcao && currentIcao !== originIcao) {
    hard.push({
      code: "wrong_phase",
      message: `Aircraft is ${input.aircraftState.label} at ${currentIcao}, not ${originIcao}.`,
    });
  }

  const dutyHits: HardReject[] = [];
  if (input.duty.status === "rest") {
    dutyHits.push({
      code: "duty_rest",
      message: `Crew is on rest — ${input.duty.certificateLabel} clock blocks assign.`,
    });
  }
  const blockMin = eteMin + repo.eteMin + MIN_TURN_MIN;
  if (input.duty.status === "on") {
    if (blockMin > input.duty.flightRemainMin) {
      dutyHits.push({
        code: "insufficient_flight_time",
        message: `Trip + reposition ${blockMin} min exceeds remaining flight time ${Math.max(0, Math.round(input.duty.flightRemainMin))} (${input.duty.certificateLabel}).`,
      });
    }
    if (blockMin > input.duty.fdpRemainMin) {
      dutyHits.push({
        code: "insufficient_duty",
        message: `Trip + reposition ${blockMin} min exceeds remaining FDP ${Math.max(0, Math.round(input.duty.fdpRemainMin))} (${input.duty.certificateLabel}).`,
      });
    }
  } else if (input.duty.status === "off") {
    if (blockMin > input.duty.limits.maxFlightTimeMin) {
      dutyHits.push({
        code: "insufficient_flight_time",
        message: `Trip + reposition ${blockMin} min exceeds max flight time ${input.duty.limits.maxFlightTimeMin}.`,
      });
    }
  }
  if (input.enforceDutyClock !== false) {
    hard.push(...dutyHits);
  } else {
    for (const hit of dutyHits) {
      factors.push({ id: "duty_advisory", delta: -8, note: `Advisory: ${hit.message}` });
    }
  }

  let score = 70;
  if (repo.nm > 0) {
    const dead = Math.min(30, repo.nm * 0.15);
    score -= dead;
    factors.push({ id: "deadhead", delta: -dead, note: `Deadhead ${repo.nm.toFixed(0)} NM / ${repo.eteMin} min.` });
    const tpen = Math.min(20, repo.eteMin * 0.05);
    score -= tpen;
    factors.push({ id: "reposition_time", delta: -tpen, note: "Reposition time." });
  } else {
    score += 15;
    factors.push({ id: "same_field", delta: 15, note: "Aircraft is already at origin." });
  }
  const originField = hydrateSceneryAirport(originIcao, input.scenery);
  const destField = hydrateSceneryAirport(destIcao, input.scenery);
  const rwyNote = runwayPicPairWarning(
    { icao: originIcao, runwayFt: originField?.runwayFt },
    { icao: destIcao, runwayFt: destField?.runwayFt },
  );
  if (rwyNote) {
    factors.push({ id: "runway_pic", delta: 0, note: rwyNote });
  }

  if (minutesToStd >= 20 && minutesToStd < 50) {
    score += 5;
    factors.push({ id: "turn", delta: 5, note: "Tight but legal turn." });
  } else if (minutesToStd >= 50) {
    score += 8;
    factors.push({ id: "turn", delta: 8, note: "Adequate turn time." });
  } else if (minutesToStd < 20 && repo.nm === 0) {
    score -= 10;
    factors.push({ id: "turn", delta: -10, note: "Short turn at field." });
  }

  const revenue = input.request.deskQuote?.revenueCad ?? 0;
  if (revenue > 0) {
    const bump = Math.min(10, revenue / 5000);
    score += bump;
    factors.push({ id: "revenue", delta: bump, note: "Rate-card revenue fit." });
  }

  if (input.duty.level === "warn") {
    score -= 12;
    factors.push({ id: "fatigue", delta: -12, note: "Duty clock yellow." });
  }
  if (input.duty.status === "on") {
    score += 5;
    factors.push({ id: "on_duty", delta: 5, note: "Crew already on duty." });
  }

  if (input.opsDesk) {
    const stops = [
      input.request.techStopIcao,
      ...(input.request.proposedLegs ?? []).map((leg) => leg.destIcao),
    ].filter((icao): icao is string => Boolean(icao));
    const report = evaluateTripConstraints({
      ops: input.opsDesk,
      aircraft: input.aircraft,
      originIcao,
      destIcao,
      departure: input.request.departureIso,
      eteMin,
      scenery: input.scenery,
      stopIcaos: stops,
      crewIds: [input.crewId, input.sicCrewId],
      paxCount: input.request.paxCount,
      cargoLb: input.request.cargoLb,
      fuelLb: input.fuelLb ?? 0,
      flightType: input.request.flightType,
    });
    const factorFor: Record<string, SoftFactorId> = {
      under_crewed: "crew_minimum",
      curfew: "curfew",
      customs: "customs",
      overweight: "weight",
    };
    for (const hit of report.hits) {
      if (hit.severity === "block") {
        hard.push({ code: hit.code, message: hit.message });
      } else {
        factors.push({ id: factorFor[hit.code] ?? "crew_minimum", delta: 0, note: hit.message });
      }
    }
  }

  const near = input.missions.filter((m) => {
    if (m.aircraftId !== input.aircraft.id) return false;
    if (m.status === "completed" || m.status === "cancelled") return false;
    const w = missionWindow(m, 0);
    const gap = Math.min(Math.abs(w.end - tripStart), Math.abs(tripEnd - w.start)) / 60_000;
    return gap < 90 && !overlapping.includes(m);
  });
  if (near.length) {
    score -= 15;
    factors.push({ id: "near_conflict", delta: -15, note: "Nearby board time on this tail." });
  }

  const hops = requestHopNm(input.request, input.scenery);
  const operating = scoreAircraftOperatingFit({
    request: input.request,
    aircraft: input.aircraft,
    ops: input.opsDesk,
    maxHopNm: hops.maxHopNm,
    tripNm: hops.tripNm,
    departureMs: tripStart,
    repositionEteMin: repo.eteMin,
  });
  for (const factor of operating.factors) {
    score += factor.delta;
    factors.push(factor);
  }
  score = Math.max(0, Math.min(100, Math.round(score)));
  if (operating.cost) {
    score += operating.cost.delta;
    factors.push(operating.cost);
    score = Math.max(0, Math.min(100, Math.round(score)));
  }
  const blocking = hard.filter((h) => h.code !== "already_assigned");
  return {
    ok: blocking.length === 0,
    hard,
    score,
    factors,
    repositionRequired: repo.nm > 8,
    repositionNm: repo.nm,
    repositionEteMin: repo.eteMin,
    currentIcao,
    originIcao,
  };
}

export function rankAssignable(
  request: CharterRequest,
  fleet: AircraftSpec[],
  rest: Omit<AssignTripInput, "request" | "aircraft">,
): RankedCandidate[] {
  return fleet
    .map((aircraft) => ({ aircraft, ...canAssign({ ...rest, request, aircraft }) }))
    .sort((a, b) => {
      if (a.ok !== b.ok) return a.ok ? -1 : 1;
      if (b.score !== a.score) return b.score - a.score;
      return hourlyCostTieBreak(a.aircraft, b.aircraft, rest.opsDesk);
    });
}

export function pickAutoAssignment(
  request: CharterRequest,
  fleet: AircraftSpec[],
  rest: Omit<AssignTripInput, "request" | "aircraft">,
): RankedCandidate | null {
  const ranked = rankAssignable(request, fleet, rest);
  return ranked.find((row) => row.ok) ?? null;
}

function crewOpenLoad(assignments: TripAssignment[], crewId: string): number {
  return openAssignmentsForCrew(assignments, crewId).length;
}

/** Rank (tail × pilot) pairs. Prefer spreading trips across pilots on different tails. */
export function rankAssignablePairs(
  request: CharterRequest,
  fleet: AircraftSpec[],
  crews: CrewCandidate[],
  rest: Omit<AssignTripInput, "request" | "aircraft" | "crewId" | "crewName" | "duty" | "ratedIcaoTypes">,
  opts?: { learnedRank?: (pair: { aircraftId: string; crewId: string }) => number },
): RankedPair[] {
  const rows: RankedPair[] = [];
  for (const crew of crews) {
    for (const aircraft of fleet) {
      const result = canAssign({
        ...rest,
        request,
        aircraft,
        crewId: crew.crewId,
        crewName: crew.crewName,
        duty: crew.duty,
        ratedIcaoTypes: crew.ratedIcaoTypes,
      });
      const load = crewOpenLoad(rest.assignments, crew.crewId);
      let score = result.score;
      const factors = [...result.factors];
      if (load > 0) {
        const delta = -Math.min(24, load * 8);
        score = Math.max(0, score + delta);
        factors.push({
          id: "crew_load",
          delta,
          note: `${crew.crewName} already has ${load} open trip${load === 1 ? "" : "s"}.`,
        });
      }
      const prefs = hydrateCrewRoutePrefs({
        routeAreaPrefs: crew.routeAreaPrefs,
        preferredTripNm: crew.preferredTripNm,
      });
      const prefDelta = scoreCrewPrefMatch(prefs, requestPrefContext(request, rest.scenery));
      if (prefDelta) {
        score = Math.max(0, Math.min(100, score + prefDelta));
        factors.push({
          id: "crew_pref",
          delta: prefDelta,
          note:
            prefDelta > 0
              ? `${crew.crewName} route prefs soft-match this RFQ.`
              : `${crew.crewName} route prefs are a weak match (company brief still wins).`,
        });
      }
      rows.push({
        ...result,
        score,
        factors,
        aircraft,
        crewId: crew.crewId,
        crewName: crew.crewName,
      });
    }
  }
  return rows.sort((a, b) => {
    if (a.ok !== b.ok) return a.ok ? -1 : 1;
    if (b.score !== a.score) return b.score - a.score;
    if (opts?.learnedRank) {
      const left = opts.learnedRank({ aircraftId: a.aircraft.id, crewId: a.crewId });
      const right = opts.learnedRank({ aircraftId: b.aircraft.id, crewId: b.crewId });
      if (left !== right) return right - left;
    }
    return hourlyCostTieBreak(a.aircraft, b.aircraft, rest.opsDesk);
  });
}

export function pickAutoCrewTail(
  request: CharterRequest,
  fleet: AircraftSpec[],
  crews: CrewCandidate[],
  rest: Omit<AssignTripInput, "request" | "aircraft" | "crewId" | "crewName" | "duty" | "ratedIcaoTypes">,
  opts?: { learnedRank?: (pair: { aircraftId: string; crewId: string }) => number },
): RankedPair | null {
  return rankAssignablePairs(request, fleet, crews, rest, opts).find((row) => row.ok) ?? null;
}

export function buildRepositionDraft(input: {
  spec: AircraftSpec;
  fromIcao: string;
  toIcao: string;
  scenery: SceneryAirport[];
  departure: Date;
  now?: Date;
  linkRequestId?: string;
  parentMissionId?: string;
}): { ok: true; draft: DraftMission } | { ok: false; error: string } {
  const now = input.now ?? new Date();
  const origin = hydrateSceneryAirport(input.fromIcao, input.scenery);
  const dest = hydrateSceneryAirport(input.toIcao, input.scenery);
  if (!origin || !dest) return { ok: false, error: `No Destinations fields for ${input.fromIcao}–${input.toIcao}.` };
  if (origin.icao === dest.icao) return { ok: false, error: "Already at origin." };
  const purpose = `Reposition / ferry ${origin.icao}–${dest.icao}`;
  const leg = computeLeg(1, origin, dest, input.spec, input.departure, 0, [], purpose);
  if (!leg) return { ok: false, error: "Could not compute reposition leg." };
  const draft: DraftMission = withLegs({
    id: newId("ferry", now),
    createdAt: now.toISOString(),
    callsign: `${input.spec.callsignPrefix || ""}${String(now.getUTCMinutes()).padStart(2, "0")}F`,
    aircraftId: input.spec.id,
    aircraftName: input.spec.name,
    registration: input.spec.registration,
    flightType: "ferry",
    originIcao: origin.icao,
    destIcao: dest.icao,
    originName: origin.name,
    destName: dest.name,
    originCity: origin.city,
    destCity: dest.city,
    distanceNm: leg.distanceNm,
    eteMin: leg.eteMin,
    fuelGal: leg.fuelGal,
    fuelLbs: leg.fuelLbs,
    trackTrue: leg.trackTrue,
    paxCount: 0,
    paxNames: [],
    scenario: `Company ferry so the charter can originate at ${dest.icao}. No charter revenue.`,
    purpose,
    departureIso: input.departure.toISOString(),
    weatherNote: "Reposition ferry",
    originMetar: leg.originMetar,
    destMetar: leg.destMetar,
    isMaintenance: false,
    originRunwayFt: origin.runwayFt,
    destRunwayFt: dest.runwayFt,
    legs: [leg],
    source: "inbox",
    inboxRequestId: input.linkRequestId,
    parentMissionId: input.parentMissionId,
  });
  return { ok: true, draft };
}

/** Deadhead home after an outbound charter. Same no-revenue rule as a reposition ferry. */
export function buildReturnEmptyDraft(input: {
  spec: AircraftSpec;
  fromIcao: string;
  toIcao: string;
  scenery: SceneryAirport[];
  departure: Date;
  now?: Date;
  linkRequestId?: string;
  parentMissionId?: string;
}): { ok: true; draft: DraftMission } | { ok: false; error: string } {
  const built = buildRepositionDraft(input);
  if (!built.ok) return built;
  return {
    ok: true,
    draft: {
      ...built.draft,
      purpose: `Empty return ferry ${input.fromIcao}–${input.toIcao}`,
      scenario: `Empty return after the outbound charter. No charter revenue unless a backhaul fill replaces this ferry.`,
      weatherNote: "Empty return ferry — no revenue",
    },
  };
}

export function appendAudit(
  audit: AssignmentAuditEvent[],
  event: Omit<AssignmentAuditEvent, "id" | "atUtc"> & { id?: string; atUtc?: string },
  now = new Date(),
): AssignmentAuditEvent[] {
  const row: AssignmentAuditEvent = {
    id: event.id ?? newId("aud", now),
    atUtc: event.atUtc ?? now.toISOString(),
    actor: event.actor,
    action: event.action,
    requestId: event.requestId,
    assignmentId: event.assignmentId,
    aircraftId: event.aircraftId,
    note: event.note,
  };
  return [row, ...audit].slice(0, 200);
}

export type AssignmentDeskSlice = {
  inbox: CharterRequest[];
  missions: Mission[];
  assignments: TripAssignment[];
  assignmentAudit: AssignmentAuditEvent[];
  declineCooldowns: DeclineCooldown[];
};

function patchInbox(inbox: CharterRequest[], id: string, patch: Partial<CharterRequest>): CharterRequest[] {
  return inbox.map((r) => (r.id === id ? { ...r, ...patch } : r));
}

export function executeAssign(input: {
  slice: AssignmentDeskSlice;
  request: CharterRequest;
  aircraft: AircraftSpec;
  result: CanAssignResult;
  source: AssignmentSource;
  crewId: string;
  crewName: string;
  sicCrewId?: string;
  sicCrewName?: string;
  mission: Mission;
  ferry?: Mission | null;
  returnFerry?: Mission | null;
  /** Cancel a pending empty return when a linked backhaul is taken. */
  cancelMissionIds?: string[];
  now?: Date;
  /** Last-writer: supersede an open assignment on the same request. */
  allowSupersede?: boolean;
}): { ok: true; slice: AssignmentDeskSlice; assignment: TripAssignment; notice: string } | { ok: false; error: string } {
  const now = input.now ?? new Date();
  const ignorable = new Set<HardRejectCode>(["already_assigned"]);
  if (input.allowSupersede) ignorable.add("already_crewed");
  const blocking = input.result.hard.filter((h) => !ignorable.has(h.code));
  if (blocking.length) {
    return { ok: false, error: blocking[0]!.message };
  }
  let assignments = input.slice.assignments;
  let audit = input.slice.assignmentAudit;
  const existing = assignmentForRequest(assignments, input.request.id);
  if (existing && existing.crewId && existing.crewId !== input.crewId && !input.allowSupersede) {
    return {
      ok: false,
      error: `Trip already assigned to ${existing.crewName || existing.crewId}. One pilot per trip — shared cockpit is not supported.`,
    };
  }
  if (existing && (existing.aircraftId !== input.aircraft.id || existing.crewId !== input.crewId)) {
    if (!input.allowSupersede) {
      return { ok: false, error: existing.status === "accepted" ? "Already accepted — dispatcher must reassign." : `Already assigned to ${existing.registration}.` };
    }
    assignments = assignments.map((a) => (a.id === existing.id ? { ...a, superseded: true, status: a.status === "accepted" ? a.status : "declined" } : a));
    audit = appendAudit(
      audit,
      {
        actor: input.source,
        action: "supersede",
        requestId: input.request.id,
        assignmentId: existing.id,
        aircraftId: existing.aircraftId,
        note: `Last-writer ${input.source} superseded ${existing.registration}.`,
      },
      now,
    );
  }

  const assignment: TripAssignment = {
    id: newId("asgn", now),
    requestId: input.request.id,
    missionId: input.mission.id,
    ferryMissionId: input.ferry?.id,
    returnFerryMissionId: input.returnFerry?.id,
    aircraftId: input.aircraft.id,
    registration: input.aircraft.registration,
    crewId: input.crewId,
    crewName: input.crewName,
    sicCrewId: input.sicCrewId?.trim() && input.sicCrewId.trim() !== input.crewId ? input.sicCrewId.trim() : undefined,
    sicCrewName: input.sicCrewName?.trim() || undefined,
    status: input.source === "pilot" ? "accepted" : "pending",
    source: input.source,
    assignedAtUtc: now.toISOString(),
    decidedAtUtc: input.source === "pilot" ? now.toISOString() : undefined,
    reasonCode: input.source === "pilot" ? "other" : undefined,
    comment: input.source === "pilot" ? "Pilot chose this trip (on-demand)." : undefined,
    repositionRequired: input.result.repositionRequired,
    repositionNm: input.result.repositionNm,
    repositionEteMin: input.result.repositionEteMin,
  };
  audit = appendAudit(
    audit,
    {
      actor: input.source,
      action: existing ? "reassign" : "assign",
      requestId: input.request.id,
      assignmentId: assignment.id,
      aircraftId: input.aircraft.id,
      note: `${input.source} assigned ${input.aircraft.registration} ${input.request.originIcao}–${input.request.destIcao}${input.result.repositionRequired ? " + ferry" : ""}${input.returnFerry ? " + empty return" : ""}.`,
    },
    now,
  );

  const cancel = new Set(input.cancelMissionIds ?? []);
  const keepIds = new Set([input.mission.id, input.ferry?.id, input.returnFerry?.id].filter(Boolean) as string[]);
  // One canonical ticket per RFQ: any other open trip still carrying this
  // request id is a leftover from the superseded assignment, charter or ferry.
  for (const m of input.slice.missions) {
    if (keepIds.has(m.id)) continue;
    if (m.inboxRequestId !== input.request.id) continue;
    if (m.isMaintenance) continue;
    if (m.status === "completed" || m.status === "cancelled") continue;
    cancel.add(m.id);
  }
  const missions = [
    input.mission,
    ...(input.ferry ? [input.ferry] : []),
    ...(input.returnFerry ? [input.returnFerry] : []),
    ...input.slice.missions
      .filter((m) => m.id !== input.mission.id && m.id !== input.ferry?.id && m.id !== input.returnFerry?.id)
      .map((m) =>
        cancel.has(m.id)
          ? stampCancel(m, {
              cancelReason: "scheduling",
              cancelReasonNote: "Replaced by new assignment",
              at: now.toISOString(),
            })
          : m,
      ),
  ];
  const inboxStatus: InboxRequestStatus = assignment.status === "accepted" ? "accepted" : "assigned";
  return {
    ok: true,
    slice: {
      inbox: patchInbox(input.slice.inbox, input.request.id, { status: inboxStatus, acceptedMissionId: input.mission.id }),
      missions,
      assignments: [assignment, ...assignments.filter((a) => a.id !== assignment.id)],
      assignmentAudit: audit,
      declineCooldowns: input.slice.declineCooldowns,
    },
    assignment,
    notice: `${assignment.status === "accepted" ? "ACCEPTED" : "ASSIGNED"}  ${input.mission.callsign}  ${input.request.originIcao}–${input.request.destIcao}  ${input.aircraft.registration}${input.ferry ? " + ferry" : ""}${input.ferry?.dispatchWarning ? ` · ${input.ferry.dispatchWarning}` : ""}`,
  };
}

export function executeRespond(input: {
  slice: AssignmentDeskSlice;
  assignmentId: string;
  status: "accepted" | "declined" | "held";
  reasonCode: AssignmentReasonCode;
  declineReasonId?: string;
  comment?: string;
  actor?: AssignmentSource | "pilot";
  now?: Date;
}): { ok: true; slice: AssignmentDeskSlice; assignment: TripAssignment; notice: string } | { ok: false; error: string } {
  const now = input.now ?? new Date();
  const current = input.slice.assignments.find((a) => a.id === input.assignmentId && !a.superseded);
  if (!current) return { ok: false, error: "Assignment is not on file." };
  if (current.status === "accepted" && input.status !== "held") {
    return { ok: false, error: "Already accepted." };
  }
  const comment = (input.comment ?? "").trim();
  if ((input.status === "declined" || input.status === "accepted") && !input.reasonCode) {
    return { ok: false, error: "Choose a reason." };
  }
  const next: TripAssignment = {
    ...current,
    status: input.status,
    reasonCode: input.reasonCode,
    declineReasonId: input.status === "accepted" ? undefined : input.declineReasonId || current.declineReasonId,
    comment: comment || undefined,
    decidedAtUtc: now.toISOString(),
  };
  const actor = input.actor ?? "pilot";
  const reasonLabel = input.status === "accepted" ? "Accepted" : ASSIGNMENT_REASON_LABEL[input.reasonCode];
  let missions = input.slice.missions;
  let inbox = input.slice.inbox;
  let cooldowns = input.slice.declineCooldowns;
  if (input.status === "declined") {
    const declineReason = cancelReasonFromAssignment(input.reasonCode);
    missions = missions.map((m) =>
      m.id === current.missionId || m.id === current.ferryMissionId || m.id === current.returnFerryMissionId
        ? {
            ...stampCancel(m, {
              cancelReason: declineReason,
              cancelReasonNote: comment || ASSIGNMENT_REASON_LABEL[input.reasonCode],
              at: now.toISOString(),
            }),
            declineReasonId: input.declineReasonId || m.declineReasonId,
          }
        : m,
    );
    inbox = patchInbox(inbox, current.requestId, { status: "new", acceptedMissionId: undefined });
    cooldowns = addDeclineCooldown(cooldowns, current.requestId, current.crewId, now);
  }
  if (input.status === "accepted") {
    inbox = patchInbox(inbox, current.requestId, { status: "accepted", acceptedMissionId: current.missionId });
    const linked = new Set(assignmentLinkedMissionIds(current));
    missions = missions.map((m) =>
      linked.has(m.id) && m.status !== "cancelled" && m.status !== "completed"
        ? {
            ...m,
            lifecycle: m.lifecycle && m.lifecycle !== "scheduled" ? m.lifecycle : "accepted",
            acceptedAt: m.acceptedAt ?? now.toISOString(),
          }
        : m,
    );
  }
  const action = input.status === "accepted" ? "accept" : input.status === "declined" ? "decline" : "hold";
  return {
    ok: true,
    slice: {
      inbox,
      missions,
      assignments: input.slice.assignments.map((a) => (a.id === next.id ? next : a)),
      assignmentAudit: appendAudit(
        input.slice.assignmentAudit,
        {
          actor,
          action,
          requestId: current.requestId,
          assignmentId: current.id,
          aircraftId: current.aircraftId,
          note: `${reasonLabel}${comment ? ` — ${comment}` : ""}`,
        },
        now,
      ),
      declineCooldowns: cooldowns,
    },
    assignment: next,
    notice: `${input.status.toUpperCase()}  ${current.registration}  ${current.requestId}`,
  };
}

export function dutyViewForAssign(roster: DutyRoster, profile: PilotProfile, now = new Date()): DutyView {
  return dutyViewFor(roster, { id: crewIdFromName(profile.name), name: profile.name }, now);
}

export function stateForAssign(
  live: AircraftState | null | undefined,
  spec: AircraftSpec,
  scenery: SceneryAirport[],
): AircraftState {
  const parked = spec.lastAirportIcao?.toUpperCase() ?? null;
  if (live?.sample && parked && isNarniaGroundSpawn(live.sample as never, parked, scenery)) {
    return {
      ...live,
      parkedIcao: parked,
      currentIcao: parked,
    };
  }
  if (live && (live.phase !== "unknown" || live.currentIcao)) {
    return {
      ...live,
      parkedIcao: parked ?? live.parkedIcao,
      currentIcao: live.currentIcao ?? parked ?? null,
    };
  }
  if (live?.sample && scenery.length) {
    return deriveAircraftState({
      sample: live.sample,
      scenery,
      parkedIcao: parked,
      live: !live.stale && live.source !== "last_known" && live.source !== "none",
    });
  }
  return { ...emptyAircraftState(), parkedIcao: parked, currentIcao: parked };
}
